import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- pre-existing direct DataGridPro use; migrate to ServerDataGrid when touched
import { DataGridPro } from '@mui/x-data-grid-pro';
import { fromJS } from 'immutable';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { fetchRawSite } from '@sophocles/entities/Site';
import { fetchProjectJobs } from '@sophocles/features/projectServices';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import { formatUserString } from '@sophocles/utils';

function JobCardList({ jobs, onJobClick, t }) {
  return (
    <Stack spacing={1}>
      {jobs.map(job => {
        const isPending = job.pending === true;
        const crewNumber = _.get(job, ['crew', 'crew_number'], 'N/A');
        const submittedBy = job.submitted_by ? formatUserString(fromJS(job.submitted_by)) : null;

        return (
          <Card key={job.id} variant="outlined">
            <CardActionArea onClick={() => onJobClick(job)}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {job.installation_date || 'N/A'}
                      </Typography>
                      {isPending ? (
                        <GridStatusCell label={t('projectServices.siteJobs.status.pending')} color="warning" />
                      ) : (
                        <GridStatusCell label={t('projectServices.siteJobs.status.completed')} color="ok" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t('projectServices.siteJobs.columns.crewNumber')}: {crewNumber}
                      {submittedBy ? ` \u00B7 ${submittedBy}` : ''}
                    </Typography>
                  </Box>
                  <ChevronRightIcon color="action" sx={{ ml: 1, flexShrink: 0 }} />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
}

function SiteJobsView() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { customerId, projectId, siteGroupId, siteId } = useParams();

  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [jobs, setJobs] = useState([]);

  // Customer-facing site term ("site"/"structure") for the job-count line and
  // empty/pending copy; the raw site payload's customer feeds the
  // CustomerType override when it carries one, else the tenant term applies.
  const siteTerm = useSiteTerm(site?.customer ?? null);

  const basePath = `/services/customer/${customerId}/project/${projectId}/sitegroup/${siteGroupId}/site/${siteId}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [siteRes, jobsRes] = await Promise.all([
          dispatch(fetchRawSite(siteId)),
          dispatch(fetchProjectJobs({ site: siteId })),
        ]);

        if (siteRes) {
          setSite(siteRes.toJS ? siteRes.toJS() : siteRes);
        }

        const jobResults = jobsRes.payload?.results || jobsRes.payload || [];
        setJobs(jobResults);
      } catch (error) {
        console.error('Error loading site jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, siteId]);

  const columns = useMemo(
    () => [
      {
        field: 'installation_date',
        headerName: t('projectServices.siteJobs.columns.installationDate'),
        flex: 1,
        minWidth: 120,
        disableColumnMenu: true,
        valueFormatter: value => value || 'N/A',
      },
      {
        field: 'crew',
        headerName: t('projectServices.siteJobs.columns.crewNumber'),
        width: 100,
        disableColumnMenu: true,
        valueGetter: value => _.get(value, 'crew_number', 'N/A'),
      },
      {
        field: 'pending',
        headerName: t('projectServices.siteJobs.columns.status'),
        width: 120,
        disableColumnMenu: true,
        renderCell: params => {
          const isPending = params.value === true;
          return isPending ? (
            <GridStatusCell label={t('projectServices.siteJobs.status.pending')} color="warning" />
          ) : (
            <GridStatusCell label={t('projectServices.siteJobs.status.completed')} color="ok" />
          );
        },
      },
      {
        field: 'submitted_by',
        headerName: t('projectServices.siteJobs.columns.submittedBy'),
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
        valueGetter: value => (value ? formatUserString(fromJS(value)) : 'Unknown'),
      },
      {
        field: 'approved_by',
        headerName: t('projectServices.siteJobs.columns.approvedBy'),
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
        valueGetter: value => (value ? formatUserString(fromJS(value)) : ''),
      },
    ],
    [t],
  );

  const sortedJobs = useMemo(
    () =>
      [...jobs].sort((a, b) => {
        const aTime = Date.parse(a.installation_date || '');
        const bTime = Date.parse(b.installation_date || '');
        const safeATime = Number.isNaN(aTime) ? 0 : aTime;
        const safeBTime = Number.isNaN(bTime) ? 0 : bTime;
        if (safeBTime !== safeATime) {
          return safeBTime - safeATime;
        }
        return (b.id || 0) - (a.id || 0);
      }),
    [jobs],
  );

  const hasPendingJob = useMemo(() => sortedJobs.some(job => job.pending === true), [sortedJobs]);
  const [pendingWarningOpen, setPendingWarningOpen] = useState(false);

  const siteName = site ? site.name : '';
  const pageTitle = loading ? <CircularProgress /> : t('projectServices.siteJobs.pageTitle', { siteName });

  const handleJobClick = job => {
    navigate(`${basePath}/job/${job.id}/`);
  };

  const handleNewJob = () => {
    if (hasPendingJob) {
      setPendingWarningOpen(true);
    } else {
      navigate(`${basePath}/`);
    }
  };

  return (
    <AppNav notFullHeight loading={loading} page="services_projects_site_jobs">
      <Box sx={{ top: '64px', mx: 2, pb: '24px' }}>
        <TitleBar showBackButton title={pageTitle} />
        <Card variant="outlined" sx={{ mx: 1, mb: 2 }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {t('projectServices.siteJobs.jobHistory')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sortedJobs.length}{' '}
                  {sortedJobs.length === 1
                    ? t('projectServices.siteJobs.jobSingular')
                    : t('projectServices.siteJobs.jobPlural')}{' '}
                  {t('projectServices.siteJobs.forThisSite', { term: siteTerm.singular.toLowerCase() })}
                </Typography>
              </Box>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleNewJob}>
                {t('projectServices.siteJobs.newJob')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
        <Box sx={{ mx: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : sortedJobs.length === 0 ? (
            <Card variant="outlined">
              <CardContent sx={{ py: 6 }}>
                <Stack alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                    }}
                  >
                    <WorkOutlineIcon fontSize="small" />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t('projectServices.siteJobs.noJobsTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('projectServices.siteJobs.noJobsDescription', { term: siteTerm.singular.toLowerCase() })}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          ) : isSmallScreen ? (
            <JobCardList jobs={sortedJobs} onJobClick={handleJobClick} t={t} />
          ) : (
            <DataGridPro
              rows={sortedJobs}
              columns={columns}
              density="compact"
              getRowHeight={() => 'auto'}
              disableColumnFilter
              disableColumnSelector
              disableRowSelectionOnClick
              initialState={{
                sorting: { sortModel: [{ field: 'installation_date', sort: 'desc' }] },
              }}
              onRowClick={params => handleJobClick(params.row)}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' },
                '& .MuiDataGrid-row': {
                  '&:hover': { backgroundColor: 'action.hover' },
                },
              }}
              hideFooter={sortedJobs.length <= 25}
              pageSizeOptions={[25, 50, 100]}
            />
          )}
        </Box>
      </Box>

      <Dialog open={pendingWarningOpen} onClose={() => setPendingWarningOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          {t('projectServices.siteJobs.pendingJobDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('projectServices.siteJobs.pendingJobDialog.body', { term: siteTerm.singular.toLowerCase() })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingWarningOpen(false)} color="inherit">
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setPendingWarningOpen(false);
              navigate(`${basePath}/`);
            }}
          >
            {t('actions.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppNav>
  );
}

export default SiteJobsView;
